import { motion } from 'framer-motion';
import { APP_VERSION_LABEL } from '@/version';
import { isSafariWebBrowser } from '@/utils/appleAuthAvailability';
import { AuthSignInButtons } from './AuthSignInButtons';
import { GoogleLogo, AppleLogo } from './authLogos';

const PRIVACY_POLICY_URL = 'https://www.re-build-me.com/p/privacy-policy-for-re-build-me-pro.html';

const cardClassName =
  'w-full max-w-md rounded-[2rem] border border-theme-secondary bg-theme-light p-6 shadow-ambient';

/**
 * Shared sign-in gate UI (auth not ready, or signed out). Centered layout; copy differs when Apple sign-in is offered (iOS / Safari).
 */
export function AppSignInCard({
  animated = false,
  canShowAppleAuth,
  acceptedTerms,
  onAcceptedTermsChange,
  firebaseDiagnostics,
  authError,
  isSigningIn,
  onGoogle,
  onApple,
  demoMode = false,
  demoEmail = '',
  demoPassword = '',
  onDemoEmailChange,
  onDemoPasswordChange,
  onDemoSignIn,
}) {
  const safariWeb = typeof window !== 'undefined' && isSafariWebBrowser();

  const intro = canShowAppleAuth ? (
    <div className="mb-4 text-center max-w-sm mx-auto space-y-2">
      <p className="font-body text-sm leading-relaxed text-secondary">
        New users, please sign in using your Apple ID. This will create your account. We never see your Apple password—Apple
        handles sign-in securely.
      </p>
      {safariWeb && firebaseDiagnostics.appleOAuthHandlerUrl ? (
        <div className="font-body text-xs leading-snug text-secondary/90 space-y-2">
          <p>
            Safari will send you to Apple, then through Firebase’s auth host (often <span className="text-secondary">.firebaseapp.com</span>
            )—that is normal. If Apple says to use a different account, the usual fix is Apple Developer → your <strong>Services ID</strong> →
            Sign in with Apple → Web → <strong>Return URLs</strong>: add this URL exactly (copy from console too):
          </p>
          <code className="block w-full rounded-lg bg-black/5 px-2 py-1.5 text-[10px] leading-snug text-primary break-all">
            {firebaseDiagnostics.appleOAuthHandlerUrl}
          </code>
        </div>
      ) : null}
    </div>
  ) : (
    <p className="font-body text-sm leading-relaxed text-secondary mb-4 text-center max-w-sm mx-auto">
      Please sign in to use the app.
    </p>
  );

  const inner = (
    <>
      <div className="flex flex-col items-center text-center mb-5">
        <p className="font-heading text-xl text-primary tracking-tight">RE-BUILD-ME-PRO</p>
      </div>
      {intro}
      <div className="mb-4 w-full px-1">
        <div className="flex w-full justify-center">
          <label className="inline-flex max-w-[min(100%,18.5rem)] cursor-pointer items-start gap-2 text-left">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={onAcceptedTermsChange}
              className="mt-0.5 shrink-0"
            />
            <span className="font-body text-xs text-secondary leading-snug">
              I agree to the{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="underline">
                Privacy Policy and Terms of Service
              </a>
              .
            </span>
          </label>
        </div>
        <p className="font-body text-[11px] text-secondary/50 mt-2 text-center w-full tabular-nums">
          {APP_VERSION_LABEL}
        </p>
      </div>
      {!firebaseDiagnostics.hasConfig && (
        <p className="font-body text-xs text-secondary mb-3 text-center">
          Firebase config missing: {firebaseDiagnostics.missingConfigKeys.join(', ')}
        </p>
      )}
      {authError && <p className="font-body text-xs text-secondary mb-3 text-center">{authError}</p>}
      <div className="flex justify-center w-full">
        <div className="w-full max-w-sm">
          <AuthSignInButtons
            acceptedTerms={acceptedTerms}
            isSigningIn={isSigningIn}
            canShowAppleAuth={canShowAppleAuth}
            onGoogle={onGoogle}
            onApple={onApple}
            GoogleLogo={GoogleLogo}
            AppleLogo={AppleLogo}
          />
        </div>
      </div>
      {demoMode ? (
        <div className="mt-4 w-full border border-theme-secondary rounded-2xl p-3">
          <p className="font-body text-xs text-secondary mb-2 text-center">App Review demo sign-in</p>
          <div className="space-y-2">
            <input
              type="email"
              autoComplete="username"
              value={demoEmail}
              onChange={(e) => onDemoEmailChange?.(e.target.value)}
              placeholder="demo email"
              className="w-full rounded-xl border border-theme-secondary bg-app px-3 py-2 text-sm"
            />
            <input
              type="password"
              autoComplete="current-password"
              value={demoPassword}
              onChange={(e) => onDemoPasswordChange?.(e.target.value)}
              placeholder="demo password"
              className="w-full rounded-xl border border-theme-secondary bg-app px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={onDemoSignIn}
              disabled={isSigningIn || !acceptedTerms}
              className="w-full rounded-full px-4 py-2 bg-theme-primary text-primary font-body text-sm disabled:opacity-50"
            >
              {isSigningIn ? 'Signing in...' : 'Sign in with Demo Account'}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (animated) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={cardClassName}>
        {inner}
      </motion.div>
    );
  }

  return <div className={cardClassName}>{inner}</div>;
}
